import { MapPriceField } from 'components/EditableFields/ProductFields'
import {
    metadataIdRenderer,
    productLabelIdRenderer,
} from 'components/FieldRenderers/Renderers/productMetadata'
import { ProductCatalogTitleRenderer } from 'components/FieldRenderers/Renderers/productMetadata'
import { Flag } from 'components/Flag'
import { COUNTRY_LABELS } from 'const/codes'
import { UNDEFINED_VALUE } from 'const/formatting'
import { isUnset } from 'helpers/utilities'
import { Field } from 'types'

import { createField, withGroupByPlaceholder } from './createField'
import { FieldCreatorOptions } from './localTypes'

export function metadataId<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: options.id ?? 'metadata_id',
        name: 'Product',
        shortName: 'Product',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata_id'],
        renderOptions: { render: (props) => metadataIdRenderer<any>(props) },
        width: 100,
    })
}

export function metadataBrand<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__brand',
        name: 'Product Brand',
        shortName: 'Product Brand',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__brand'],
        width: 100,
    })
}

export function metadataTitle<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__title',
        name: 'Product Title',
        shortName: 'Product Title',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__title'],
        width: 100,
    })
}

export function productCatalogMetadataTitle<RecordType>(
    options: FieldCreatorOptions<RecordType> & {
        linkToProductDetails?: boolean
    } = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: options.id ?? 'metadata_id',
        name: 'Product',
        shortName: 'Product',
        minWidth: options.minWidth ?? 80,
        dataIndex: options.dataIndex ?? ['metadata_id'],
        renderOptions: {
            render: (props) =>
                ProductCatalogTitleRenderer<any>({
                    ...props,
                    showImage: options.showImage,
                    linkToProductDetails: options.linkToProductDetails ?? false,
                }),
        },
        width: 300,
        exportIndex: 7,
    })
}

// product_info is just a Banner alias for product_id; wrap any product-
// rendering field with this so the group by is selectable across data sources.
export function asProductInfoPlaceholder<RecordType>(
    field: Field<RecordType>
): Field<RecordType> {
    return withGroupByPlaceholder(field, {
        id: 'product_info',
        slug: 'product-info',
        displayName: 'Product (Image + Title + ASIN)',
        shortName: 'Product',
        definition: 'Displays the product information.',
        supportsTotal: true,
    })
}

export function productInfoPlaceholder<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return asProductInfoPlaceholder(
        productCatalogMetadataTitle({
            ...options,
            minWidth: options.minWidth ?? 120,
        })
    )
}

export function metadataMarketplace<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__marketplace',
        name: 'Marketplace',
        shortName: 'Marketplace',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__marketplace'],
        width: 125,
        renderOptions: options.renderOptions ?? {
            render: ({ value }) =>
                isUnset(value) ? (
                    UNDEFINED_VALUE
                ) : (
                    <span className="fg-flag-container">
                        <Flag country={value} />
                        {COUNTRY_LABELS[value]}
                    </span>
                ),
        },
    })
}

export function productLabelId<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'product__label_id',
        name: 'Product Label',
        shortName: 'Product Label',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['product__label_id'],
        renderOptions: {
            render: (props) => productLabelIdRenderer<any>(props),
        },
        width: 100,
        sorter: options.sorter ?? false,
    })
}

export function metadataUpc<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__upc',
        name: 'UPC',
        shortName: 'UPC',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__upc'],
        width: 100,
    })
}

export function metadataEan<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__ean',
        name: 'EAN',
        shortName: 'EAN',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__ean'],
        width: 100,
    })
}

export function metadataModelNumber<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'metadata__model_number',
        name: 'Model Number',
        shortName: 'Model Number',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__model_number'],
        width: 100,
    })
}

export function metadataProductCatalogParentCategory<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: options.id ?? 'metadata__category',
        name: options.name ?? 'Parent category',
        shortName: options.shortName ?? 'Parent cat.',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__category'],
        width: 140,
        isVisible: options.isVisible ?? false,
    })
}

export function metadataProductCatalogCategoryNodeName<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: options.id ?? 'metadata__category_node_name',
        name: options.name ?? 'Category',
        shortName: options.shortName ?? 'Category',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['metadata__category_node_name'],
        width: 160,
        isVisible: options.isVisible ?? false,
    })
}

export function metadataMapPrice<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'map_price',
        name: 'MAP',
        shortName: 'MAP',
        minWidth: 80,
        dataIndex: options.dataIndex ?? ['map_price'],
        width: 120,
        renderOptions: options.renderOptions ?? {
            render: ({ record, rowIndex, updateRecord }) => (
                <MapPriceField
                    record={
                        record as Parameters<typeof MapPriceField>[0]['record']
                    }
                    rowIndex={rowIndex}
                    updateRecord={
                        updateRecord as Parameters<
                            typeof MapPriceField
                        >[0]['updateRecord']
                    }
                />
            ),
        },
    })
}

export function metadataCurrency<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'currency',
        name: 'Currency',
        shortName: 'Currency',
        dataIndex: options.dataIndex ?? ['currency'],
        width: 90,
        isVisible: options.isVisible ?? false,
    })
}
